import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Trash2 } from "lucide-react";

export default function AdminComments() {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    http
      .get("/admin/comments")
      .then(({ data }) => setComments(data.comments || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const del = useCallback(
    async (id) => {
      if (!window.confirm("Delete this comment? This cannot be undone.")) return;
      try {
        await http.delete(`/admin/comments/${id}`);
        toast.success("Comment deleted.");
        setReloadKey((k) => k + 1);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    },
    []
  );

  let body;
  if (loading) {
    body = (
      <div className="p-8 text-center text-[#66726d] font-mono text-xs uppercase tracking-widest animate-pulse">
        loading…
      </div>
    );
  } else if (comments.length === 0) {
    body = <div className="p-8 text-center text-[#66726d]">No comments yet.</div>;
  } else {
    body = comments.map((c) => (
      <div
        key={c.id}
        className="px-5 py-5 border-t"
        style={{ borderColor: "var(--border)" }}
        data-testid={`comment-row-${c.id}`}
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="min-w-0">
            <div className="font-serif text-lg text-[#151e1a]">{c.author}</div>
            {c.email && (
              <div className="font-mono text-[10px] text-[#66726d] break-all">{c.email}</div>
            )}
          </div>
          <button
            onClick={() => del(c.id)}
            className="shrink-0 p-2 text-[#1e4a35] hover:text-[#10301f]"
            aria-label="Delete comment"
            data-testid={`delete-comment-${c.id}`}
          >
            <Trash2 size={15} />
          </button>
        </div>
        <p className="text-[#151e1a] font-light leading-relaxed whitespace-pre-wrap mb-3">
          {c.content}
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d]">
          <span>{new Date(c.created_at).toLocaleString()}</span>
          <span>·</span>
          <span>on</span>
          <Link
            to={`/blog/${c.post_slug}`}
            className="text-[#1e4a35] hover:text-[#1e4a35] normal-case tracking-normal font-serif text-xs"
            data-testid={`comment-post-link-${c.id}`}
          >
            {c.post_title}
          </Link>
        </div>
      </div>
    ));
  }

  return (
    <div
      className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20"
      data-testid="admin-comments-page"
    >
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#66726d] hover:text-[#1e4a35] mb-6"
        data-testid="back-to-admin"
      >
        <ChevronLeft size={14} /> Back to dashboard
      </Link>

      <div className="mb-12">
        <p className="overline mb-3">Admin · Moderation</p>
        <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
          Reader Comments
        </h1>
        <p className="mt-4 text-[#404e48] max-w-lg font-light">
          {loading
            ? "…"
            : `${comments.length} ${comments.length === 1 ? "comment" : "comments"} across all posts. Delete any that don't belong.`}
        </p>
      </div>

      <div className="border" style={{ borderColor: "var(--border)" }}>
        {body}
      </div>
    </div>
  );
}
